import dayjs from 'dayjs';
import * as Yup from 'yup';
import { MAX_DISCOUNT_AMOUNT_TYPE_VALUES } from '@tiendanube/common/src/domains/marketing/coupons/types';
import { Scope } from 'domains/Marketing/enums';

const TIME_FORMAT = 'HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';
export const validationSchema = (currentUses: number) =>
  Yup.object().shape({
    couponCode: Yup.string()
      .required('coupons.couponForm.errors.code.required')
      .test('length', 'coupons.couponForm.errors.code.length', (value) => {
        if (!value) return true;
        return value.length <= 30;
      }),
    discountType: Yup.string(),
    valuePercentage: Yup.string()
      .test(
        'requiredPercentage',
        'coupons.couponForm.errors.valuePercentage.required',
        (_, { parent: { discountType, valuePercentage } }) =>
          (discountType === 'percentage' && Number(valuePercentage) > 0) ||
          discountType !== 'percentage',
      )
      .test(
        'maxPercentage',
        'coupons.couponForm.errors.valuePercentage.valueMax',
        (_, { parent: { discountType, valuePercentage } }) =>
          (discountType === 'percentage' && Number(valuePercentage) <= 100) ||
          discountType !== 'percentage',
      ),
    valueAbsolute: Yup.string().test(
      'requiredAbsolute',
      'coupons.couponForm.errors.valueAbsolute.required',
      (_, { parent: { discountType, valueAbsolute } }) =>
        (discountType === 'absolute' && Number(valueAbsolute) > 0) ||
        discountType !== 'absolute',
    ),
    maxDiscountAmount: Yup.string()
      .test(
        'requiredMaxDiscountAmount',
        'coupons.couponForm.errors.maxDiscountAmount.required',
        (
          _,
          {
            parent: { discountType, maxDiscountAmountType, maxDiscountAmount },
          },
        ) => {
          if (
            discountType !== 'percentage' ||
            maxDiscountAmountType !== 'limited'
          ) {
            return true;
          }
          return maxDiscountAmount && String(maxDiscountAmount).trim() !== '';
        },
      )
      .test(
        'validMaxDiscountAmount',
        'coupons.couponForm.errors.maxDiscountAmount.invalid',
        (
          _,
          {
            parent: { discountType, maxDiscountAmountType, maxDiscountAmount },
          },
        ) => {
          if (
            discountType !== 'percentage' ||
            maxDiscountAmountType !== 'limited'
          ) {
            return true;
          }
          if (!maxDiscountAmount || String(maxDiscountAmount).trim() === '') {
            return true;
          }
          return (
            Number.isFinite(Number(maxDiscountAmount)) &&
            Number(maxDiscountAmount) >= 0.01
          );
        },
      ),
    maxDiscountAmountType: Yup.string().oneOf([
      ...MAX_DISCOUNT_AMOUNT_TYPE_VALUES,
    ]),
    limitedType: Yup.string(),
    maxUsesPerClient: Yup.string().test(
      'requiredMaxUsesPerClient',
      'coupons.couponForm.errors.maxUsesPerClient.required',
      (_, { parent: { maxUsesPerClient, clientLimitationType } }) =>
        (Number(maxUsesPerClient) >= 1 && clientLimitationType === 'limited') ||
        clientLimitationType !== 'limited',
    ),
    maxUses: Yup.string()
      .test(
        'requiredMaxUses',
        'coupons.couponForm.errors.maxUses.required',
        (_, { parent: { limitedType, maxUses } }) =>
          (limitedType === 'limited' && Number(maxUses) > 0) ||
          limitedType !== 'limited',
      )
      .test(
        'requiredMaxUsesGreaterThanCurrentUses',
        'coupons.couponForm.errors.maxUses.greaterThanCurrentUses',
        (_, { parent: { limitedType, maxUses } }) =>
          (limitedType === 'limited' && Number(maxUses) >= currentUses) ||
          limitedType !== 'limited',
      ),
    startDate: Yup.string().test(
      'formatStartDate',
      'coupons.couponForm.errors.startDate.format',
      (value) => dayjs(value, DATE_FORMAT).isValid(),
    ),
    endDate: Yup.string().test(
      'formatEndDate',
      'coupons.couponForm.errors.endDate.format',
      (value) => dayjs(value, DATE_FORMAT).isValid(),
    ),
    startTime: Yup.string().test(
      'validTimeFormat',
      'errors.invalidTimeFormat',
      (value) =>
        value === undefined || dayjs(value, TIME_FORMAT, true).isValid(),
    ),
    endTime: Yup.string()
      .test(
        'validTimeFormat',
        'errors.invalidTimeFormat',
        (value) =>
          value === undefined || dayjs(value, TIME_FORMAT, true).isValid(),
      )
      .test('endAfterStart', 'errors.endAfterStart', (_, context) => {
        const { startDate, startTime, endDate, endTime } = context.parent;

        if (!startDate || !startTime || !endDate || !endTime) return true;

        const start = dayjs(`${startDate}T${startTime}`);
        const end = dayjs(`${endDate}T${endTime}`);

        return start.isBefore(end);
      }),
    categories: Yup.array().when('scopeType', {
      is: Scope.CATEGORIES,
      then: Yup.array()
        .min(1, 'coupons.couponForm.errors.categories.required')
        .max(100, 'coupons.couponForm.errors.categories.tooMany'),
    }),
    products: Yup.array().when('scopeType', {
      is: Scope.PRODUCTS,
      then: Yup.array()
        .min(1, 'coupons.couponForm.errors.products.required')
        .max(100, 'coupons.couponForm.errors.products.tooMany'),
    }),
  });
